import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { Bar } from 'react-chartjs-2';
import { Skeleton } from '@material-ui/lab';
import { Divider, Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { HttpService } from "../../apiAuthorization/httpService";
import { OfflineService } from "../../offline/storageService/offlineService";
import { Context } from "../contexts/siteContext.js";
import { Theme } from "../contexts/themeContext.js";
import { GlobalTheme, TileTheme} from "../../layouts/theme";
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import Card from "../card/card.js";
import CardHeader from "../card/cardHeader.js";
import CardBody from "../card/cardBody.js";
import CardContent from '@material-ui/core/CardContent';

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
        position: "absolute"
    }
}));

export default function VisitationEVSChart(props) {
    const classes = useStyles();
    const tileClasses = TileTheme();
    const offlineService = new OfflineService();
    const { defaultSite, siteLoading } = useContext(Context);
    const { theme } = useContext(Theme);
    const [loading, setLoading] = useState(true);
    const [backDrop, setBackDrop] = useState(false);
    const [totalNewPatient, setTotalNewPatient] = useState(0);
    const [totalLOS, setTotalLOS] = useState(0)
    const [totalFollowUp, setTotalFollowUp] = useState(0);
    const currentTheme = GlobalTheme[theme];

    const getVisitationEVSSummary = () => {
        setBackDrop(true);
        HttpService.post("/VisitationEVS/GetVisitationEVSSummary", {
            Date: ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite),
            SiteId: defaultSite.id
        })
        .then(async (result) => {
            if (result && result.offline) {
                let offlineResult = await offlineService.getOfflinesBySite(defaultSite.id, "VisitationEVSSummary");
                result = offlineResult.OfflineData;
            }
            else {
                offlineService.removeOfflineBySite(defaultSite.id, "VisitationEVSSummary");
                offlineService.addOffline(defaultSite.id, "VisitationEVSSummary", result);
            }

            if (result.data !== null) {
                setTotalNewPatient(result.data.TotalNewPatient);
                setTotalLOS(result.data.TotalLOS);
                setTotalFollowUp(result.data.TotalFollowUp);
                setLoading(false);
                setBackDrop(false);
            }
        },
        (error) => {
            console.log(error);
            setBackDrop(false);
        });
    };

    const chartData = {
        labels: [],
        datasets: [
            {
                label: 'New',
                backgroundColor: '#367CF7',
                borderWidth: 1,
                data: [totalNewPatient],
                minBarLength: 2,
                barPercentage: 0.7,
                categoryPercentage: 0.5
            },
            {
                label: 'LOS',
                backgroundColor: '#88C6FF',
                borderWidth: 1,
                data: [totalLOS],
                minBarLength: 2,
                barPercentage: 0.7,
                categoryPercentage: 0.5
            },
            {
                label: 'SRFU',
                backgroundColor: '#EAA93C',
                borderWidth: 1,
                data: [totalFollowUp],
                minBarLength: 2,
                barPercentage: 0.7,
                categoryPercentage: 0.5
            }
        ]
    };

    const options = {
        legend: {
            position: 'top',
            display: false,
            labels: {
                boxWidth: 15
            }
        },
        layout: {
            padding: {
                left: 30,
                right: 50,
                top: 0,
                bottom: -20
            }
        },
        scales: {
            xAxes: [{
                ticks: { beginAtZero: true, display: true },
                gridLines: {
                    display: true,
                    drawBorder: true
                }
            }],
            yAxes: [{
                ticks: { beginAtZero: true, display: false },
                gridLines: {
                    display: true,
                    drawBorder: true
                }
            }]
        },
        tooltips: {
            displayColors: false,
            titleFontSize: 12,
            bodyFontSize: 12,
            xPadding: 5,
            yPadding: 5
        }
    }

    useEffect(() => {
        if (!siteLoading)
            getVisitationEVSSummary();
         //eslint-disable-next-line 
    }, [props.mode, defaultSite, siteLoading]);

    return (<>
        <div style={{ position: "relative" }}>
            <Link to={{ pathname: "/VisitationEVSList" }}>
                <Backdrop className={classes.backdrop} id="visitationEVSBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Card chart color="primary" className={tileClasses.card} style={{ background: currentTheme.background, color: currentTheme.text }} >
                    <CardContent className={tileClasses.cardContent}>
                        <CardHeader>
                            <div className={tileClasses.title}>Visitation EVS</div>
                            {loading === true ? (<Skeleton variant="rect" height={76} />) : (
                                <Bar data={chartData} options={options} height={76} />
                            )}
                        </CardHeader>
                        <Divider />
                        <CardBody>
                            <div className={tileClasses.subTitle}>Daily Summary</div>
                            {loading === true ? (<div><Skeleton variant="text" /><Skeleton variant="text" /></div>) : (
                                <div className="d-flex flex-column">
                                    <label className={tileClasses.optionText}><span style={{ background: '#367CF7' }} className={ tileClasses.optionBlock}></span>New Patients: {totalNewPatient}</label>
                                    <label className={tileClasses.optionText}><span style={{ background: '#88C6FF' }} className={ tileClasses.optionBlock}></span>Length of Stay: {totalLOS}</label>
                                    <label className={tileClasses.optionText}><span style={{ background: '#EAA93C' }} className={ tileClasses.optionBlock}></span>SR Follow Up: {totalFollowUp}</label>
                                </div>
                            )}
                        </CardBody>
                    </CardContent>
                </Card>
            </Link>
        </div>
    </>
    );
}
